import React, { useState } from 'react';
import { DollarSign, Users, Handshake, Clock, Send, Phone, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const benefits = [
  { icon: DollarSign, title: 'Earn Referral Fees', desc: 'Receive a competitive referral fee on every loan that closes. Your clients get great financing and you get paid.' },
  { icon: Clock, title: 'Fast Closings', desc: 'We close in as few as 10 business days. Your clients stay on schedule and you close your transaction faster.' },
  { icon: Users, title: 'Dedicated Support', desc: 'You get a direct point of contact — no 1-800 numbers or call centers. We keep you in the loop from application to close.' },
  { icon: Handshake, title: 'Protect Your Client Relationship', desc: 'We work with you, not around you. Your client stays your client. We handle the financing, you handle the real estate.' },
];

const idealClients = [
  'Buyers purchasing investment properties (rentals, fix & flips, STRs)',
  'Sellers whose buyers need fast, non-conventional financing to close',
  'Investors looking to refinance or pull cash out of existing properties',
  'Clients who have been turned down by traditional banks',
  'Anyone buying or selling rental property who needs DSCR financing',
];

const AgentsPage = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', brokerage: '', market: '', investorClients: '', message: ''
  });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/agent-referral`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', brokerage: '', market: '', investorClients: '', message: '' });
      setSmsOptIn(false);
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Real Estate Agent Referral Program | Close Now Lending"
        description="Earn referral fees by partnering with Close Now Lending. Send us your investor clients who need DSCR, hard money, or bridge loan financing. Fast closings, dedicated support, and competitive payouts."
        keywords="real estate agent referral program, agent referral fees, investor client financing, DSCR loan referral, Close Now Lending agent program, real estate agent partnership"
        url="/agents"
      />
      <Header />

      {/* Hero */}
      <section className="bg-slate-900 pt-28 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-block bg-teal-500/20 border border-teal-400/30 rounded-full px-4 py-1.5 mb-5">
              <span className="text-teal-300 text-sm font-semibold tracking-wide">AGENT REFERRAL PROGRAM</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Your Clients Need Financing.<br />
              <span className="text-teal-400">You Earn the Referral Fee.</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl leading-relaxed">
              Partner with Close Now Lending Corp and earn a referral fee every time one of your clients closes a loan with us. Whether they're buying an investment property, selling to an investor, or need DSCR financing — we make it easy for you and your clients.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Three simple steps to start earning referral fees.</p>
          </div>
          <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Refer Your Client', desc: 'When your buyer or seller needs investment property financing, introduce them to Close Now Lending.' },
              { step: '02', title: 'We Handle the Loan', desc: 'Our team works directly with your client to structure, underwrite, and close the loan — fast.' },
              { step: '03', title: 'You Get Paid', desc: 'When the loan closes, you receive your referral fee. Simple, transparent, no surprises.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">{item.step}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3">Why Agents Partner With Us</h2>
          </div>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <b.icon className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{b.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal Client Types */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex flex-col lg:flex-row gap-12 items-start">
            <div className="flex-1">
              <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3">Which of Your Clients Can We Help?</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                If any of your clients fit these profiles, they could benefit from our loan products — and you could earn a referral fee.
              </p>
              <ul className="space-y-3">
                {idealClients.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1 bg-slate-900 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-4">Loan Products Your Clients Can Access</h3>
              <div className="space-y-3">
                {[
                  { name: 'DSCR Loans', desc: 'Qualify on rental income, not personal income' },
                  { name: 'Hard Money', desc: 'Fast closings for time-sensitive deals' },
                  { name: 'Fix & Flip', desc: 'Finance the purchase + renovation' },
                  { name: 'Bridge Loans', desc: 'Short-term capital for transitional deals' },
                  { name: 'New Construction', desc: 'Ground-up build financing' },
                  { name: 'Short-Term Rental', desc: 'Airbnb & VRBO property financing' },
                ].map((p) => (
                  <div key={p.name} className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-3">
                    <ArrowRight className="h-4 w-4 text-teal-400 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-sm">{p.name}</span>
                      <span className="text-gray-400 text-xs ml-2">— {p.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-slate-50" id="agent-form">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3">Become a Referral Partner</h2>
              <p className="text-gray-600">Fill out the form below and we'll reach out to share more details about our agent referral program.</p>
            </div>

            {success ? (
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center" data-testid="agent-success">
                <CheckCircle className="h-12 w-12 text-teal-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">We Got Your Info!</h3>
                <p className="text-gray-600">A member of our team will reach out within 24 hours to discuss the referral program.</p>
                <a href="tel:+19034762937">
                  <Button className="mt-4 bg-teal-600 hover:bg-teal-700">
                    <Phone className="h-4 w-4 mr-2" />Call Us Now: 903-476-2937
                  </Button>
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm space-y-5" data-testid="agent-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Full Name *</Label>
                    <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="mt-1" data-testid="agent-name" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Email *</Label>
                    <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required className="mt-1" data-testid="agent-email" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Phone *</Label>
                    <Input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required className="mt-1" data-testid="agent-phone" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Brokerage</Label>
                    <Input value={formData.brokerage} onChange={e => setFormData({...formData, brokerage: e.target.value})} placeholder="e.g., Keller Williams, eXp Realty" className="mt-1" data-testid="agent-brokerage" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Market / Area</Label>
                    <Input value={formData.market} onChange={e => setFormData({...formData, market: e.target.value})} placeholder="e.g., Houston, Dallas-Fort Worth" className="mt-1" data-testid="agent-market" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Do you work with investor clients?</Label>
                    <select
                      value={formData.investorClients}
                      onChange={e => setFormData({...formData, investorClients: e.target.value})}
                      className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="agent-investor-clients"
                    >
                      <option value="">Select...</option>
                      <option value="yes-regularly">Yes, regularly</option>
                      <option value="yes-occasionally">Yes, occasionally</option>
                      <option value="not-yet">Not yet, but interested</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-gray-700">Anything you'd like us to know?</Label>
                  <textarea
                    value={formData.message}
                    onChange={e => setFormData({...formData, message: e.target.value})}
                    rows={3}
                    className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    data-testid="agent-message"
                  />
                </div>

                {error && <p className="text-red-600 text-sm">{error}</p>}

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="sms-optin-agent"
                    checked={smsOptIn}
                    onChange={e => setSmsOptIn(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    required
                    data-testid="agent-sms-optin"
                  />
                  <label htmlFor="sms-optin-agent" className="text-xs text-gray-500 leading-relaxed">
                    I agree to be contacted by Close Now Lending Corp via call, email, and text for financial services. To opt out of text messages, you can reply 'stop' at any time or reply 'help' for assistance. You can also select the unsubscribe link in the emails. Message and data rates may apply. Message frequency may vary. <a href="/privacy" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a> | <a href="/terms" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  </label>
                </div>

                <Button type="submit" disabled={submitting} className="w-full bg-teal-600 hover:bg-teal-700 text-white py-3" data-testid="agent-submit">
                  {submitting ? 'Sending...' : <><Send className="h-4 w-4 mr-2" />Request More Information</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AgentsPage;
